import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { closeContract } from "@common/contracts/close.contract";
import type {
    ClientsCloseOverviewDetailsResponse,
    CloseOverviewCloseRow,
    CloseOverviewMember,
    CloseOverviewOpenRoles,
} from "@common/schemas/clientsCloseOverview.schema";
import type { UpdateCloseBody } from "@common/schemas/close.schema";
import { extractApiErrorMessage } from "@api/apiError";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { useScope } from "@state/scopeContext.lib";
import { parseCloseId } from "@hooks/useOpenWorkRow";
import { invalidateWorkspaceTaskQueries } from "@api/taskQueryKeys";
import { invalidateWorkspaceHistoryQueries } from "@api/auditLog.api";
import {
    invalidateClientsCloseOverviewQueries,
    workspaceQueryKeys,
} from "@api/workspace.api";

const closeClient = getBackendAPIClient(closeContract);

export const UPDATE_WORK_ITEM_FAILED = "Failed to update work item";

/** The message a 400 response gave for `error`, else `fallback`: what a failed work item change shows. */
export const getWorkItemErrorMessage = <TError>(
    error: TError,
    fallback: string,
) =>
    isAxiosError(error) && error.response?.status === 400
        ? extractApiErrorMessage(error.response.data, fallback)
        : fallback;

const UPDATE_WORK_ITEM_KEY = ["updateWorkItem"];

/** A role on a work item's engagement team, by its member list's name. */
export type WorkRoleField = "preparers" | "reviewers" | "managers";

/** A work item's work date. */
export type WorkDateField = "workStartDate" | "workDeliveryDate";

/** One edit to a work item: a work date, or who holds one of its roles. */
export type WorkItemEdit =
    | { field: WorkDateField; date: string }
    | { field: WorkRoleField; member: CloseOverviewMember };

type WorkItemFields = Pick<
    CloseOverviewCloseRow,
    WorkDateField | WorkRoleField | "openRoles"
>;

/** The fields a work item edit can change, from a cached close or a saved one. */
const pickWorkItemFields = ({
    workStartDate,
    workDeliveryDate,
    preparers,
    reviewers,
    managers,
    openRoles,
}: WorkItemFields): WorkItemFields => ({
    workStartDate,
    workDeliveryDate,
    preparers,
    reviewers,
    managers,
    openRoles,
});

interface UpdateWorkItemVariables {
    /** The row's close id as the overview has it (`close:<uuid>`). */
    closeId: string;
    /** The work item's client, whose task reads refetch when Work start moves. */
    workspaceId: string;
    edit: WorkItemEdit;
}

const ROLE_ID_FIELDS = {
    preparers: "preparerId",
    reviewers: "reviewerId",
    managers: "managerId",
} as const;

/** Each role's entry in `openRoles`: the open tasks a change to it reassigns. */
export const OPEN_ROLE_FIELDS: Record<
    WorkRoleField,
    keyof CloseOverviewOpenRoles
> = {
    preparers: "preparer",
    reviewers: "reviewer",
    managers: "manager",
};

const toUpdateCloseBody = (edit: WorkItemEdit): UpdateCloseBody =>
    "date" in edit
        ? { [edit.field]: edit.date }
        : { [ROLE_ID_FIELDS[edit.field]]: edit.member.userId };

/** What `edit` shows on a cached close until the save answers. */
const toFields =
    (edit: WorkItemEdit) =>
    ({ openRoles }: WorkItemFields): Partial<WorkItemFields> =>
        "date" in edit
            ? { [edit.field]: edit.date }
            : {
                  [edit.field]: [edit.member],
                  openRoles: {
                      ...openRoles,
                      [OPEN_ROLE_FIELDS[edit.field]]: {
                          assigneeIds: [edit.member.userId],
                          hasOpenTasks: true,
                      },
                  },
              };

/**
 * `source`'s values for what `edit` changes: its date, or its role and that
 * role's open tasks. Other fields keep their current values, so settling one
 * edit, saved or failed, doesn't undo another one still in flight.
 */
const pickEditedFields =
    (edit: WorkItemEdit, source: WorkItemFields) =>
    (current: WorkItemFields): Partial<WorkItemFields> => {
        if ("date" in edit) {
            return { [edit.field]: source[edit.field] };
        }
        const openRole = OPEN_ROLE_FIELDS[edit.field];
        return {
            [edit.field]: source[edit.field],
            openRoles: {
                ...current.openRoles,
                [openRole]: source.openRoles[openRole],
            },
        };
    };

const patchClose = (
    data: ClientsCloseOverviewDetailsResponse | undefined,
    closeId: string,
    update: (close: CloseOverviewCloseRow) => Partial<WorkItemFields>,
) =>
    data && {
        workspaces: data.workspaces.map((workspace) => ({
            ...workspace,
            children: workspace.children.map((entity) => ({
                ...entity,
                children: entity.children.map((close) =>
                    close.id === closeId
                        ? { ...close, ...update(close) }
                        : close,
                ),
            })),
        })),
    };

const findCloseFields = (
    datas: Array<ClientsCloseOverviewDetailsResponse | undefined>,
    closeId: string,
): WorkItemFields | undefined => {
    for (const data of datas) {
        for (const workspace of data?.workspaces ?? []) {
            for (const entity of workspace.children) {
                const close = entity.children.find(({ id }) => id === closeId);
                if (close) {
                    return pickWorkItemFields(close);
                }
            }
        }
    }
    return undefined;
};

/**
 * Saves one edit to a work item. Every cached Work overview shows it right
 * away and gets the edited fields' old values back if the save fails; the
 * error carries the message to show. Edits can run in parallel (bulk): the
 * overview refetches once the last one settles. A saved Work start or role
 * also refetches the client's tasks: the backend moves their due dates or
 * their assignee with it.
 */
export function useUpdateWorkItem() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();
    const detailsKey = workspaceQueryKeys.clientsCloseOverviewDetails();
    const patchDetails = (
        closeId: string,
        update: (close: CloseOverviewCloseRow) => Partial<WorkItemFields>,
    ) =>
        queryClient.setQueriesData<ClientsCloseOverviewDetailsResponse>(
            { queryKey: detailsKey },
            (data) => patchClose(data, closeId, update),
        );

    return useMutation({
        mutationKey: UPDATE_WORK_ITEM_KEY,
        mutationFn: async ({ closeId, edit }: UpdateWorkItemVariables) => {
            const id = parseCloseId(closeId);
            if (!organizationId || !id) {
                throw new Error(UPDATE_WORK_ITEM_FAILED);
            }
            try {
                const result = await closeClient.updateClose({
                    params: { organizationId, closeId: id },
                    body: toUpdateCloseBody(edit),
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                });
                return result.body;
            } catch (error) {
                throw new Error(
                    getWorkItemErrorMessage(error, UPDATE_WORK_ITEM_FAILED),
                );
            }
        },
        onMutate: async ({ closeId, edit }) => {
            await queryClient.cancelQueries({ queryKey: detailsKey });
            const previous = findCloseFields(
                queryClient
                    .getQueriesData<ClientsCloseOverviewDetailsResponse>({
                        queryKey: detailsKey,
                    })
                    .map(([, data]) => data),
                closeId,
            );
            patchDetails(closeId, toFields(edit));
            return { previous };
        },
        onSuccess: (saved, { closeId, workspaceId, edit }) => {
            patchDetails(
                closeId,
                pickEditedFields(edit, pickWorkItemFields(saved)),
            );
            if (edit.field !== "workDeliveryDate") {
                void invalidateWorkspaceTaskQueries(queryClient, workspaceId);
            }
            void invalidateWorkspaceHistoryQueries(queryClient, workspaceId);
        },
        onError: (_error, { closeId, edit }, context) => {
            if (context?.previous) {
                patchDetails(closeId, pickEditedFields(edit, context.previous));
            }
        },
        onSettled: () => {
            if (
                queryClient.isMutating({
                    mutationKey: UPDATE_WORK_ITEM_KEY,
                }) === 1
            ) {
                invalidateClientsCloseOverviewQueries(queryClient);
            }
        },
    });
}
